import React, { useContext, memo, useMemo, useRef, useState } from 'react';

import isEqual from 'lodash/isEqual';

import {
  Card,
  CardContent,
  CardHeader,
  CheckCircleIcon,
  CloseIcon,
  Typography,
  FileCopyIcon,
  Grid,
  IconButton,
  Stack,
  UnpublishedIcon,
  EditIcon,
} from '@gogame/components';
import { FormField, FormFieldGroup, FormContext } from '@gogame/form';

import { loadParameters } from 'scenes/Integrations/sources/postbacks/fields';

import {
  IS_FINAL,
  REQUEST_URL,
  STRATEGIES,
  CONDITIONS,
  TARGET,
  ENTITIES,
} from '../constants';
import Conditions from './Conditions';
import IconCheckbox from './IconCheckbox';
import PreviewEntities from './PreviewEntities';
import RequestURLPicker from './RequestURLPicker';

export const Strategy = memo(
  ({
    onCopyStrategy,
    index,
    strategy,
    remove,
    value,
    targets,
    isEditMode: isEditModeProps = true,
  }) => {
    const ref = useRef(null);
    const [isEditMode, setIsEditMode] = useState(isEditModeProps);
    const { values } = useContext(FormContext);
    const stategy = values[STRATEGIES][index];

    const TargetsMap = useMemo(() => {
      if (!targets) {
        return {};
      }
      return targets.reduce((result, current) => {
        return { ...result, [current.id]: current.name };
      }, {});
    }, [targets]);

    return (
      <FormFieldGroup name={`${STRATEGIES}.[${index}]`} key={index}>
        <Grid item xs={12} ref={ref}>
          <Card elevation={6}>
            <CardHeader
              title={`Strategy ${index + 1}`}
              action={
                <Stack direction={'row'} spacing={1}>
                  <IconCheckbox
                    name={IS_FINAL}
                    iconChecked={<CheckCircleIcon />}
                    iconUnchecked={<UnpublishedIcon />}
                    tooltip={'Is Final'}
                    padding={0}
                  />
                  <IconButton
                    icon={<FileCopyIcon />}
                    tooltip={'Duplicate'}
                    onClick={() => onCopyStrategy(strategy)}
                  />
                  {!isEditMode ? (
                    <IconButton
                      icon={<EditIcon />}
                      tooltip={'Edit'}
                      onClick={() => setIsEditMode(true)}
                    />
                  ) : null}
                  <IconButton
                    icon={<CloseIcon />}
                    tooltip={'Remove'}
                    onClick={() => remove(index)}
                    disabled={value.length === 1}
                  />
                </Stack>
              }
            />
            <CardContent>
              {isEditMode ? (
                <Grid container spacing={3}>
                  <Grid item xs={11}>
                    <FormField.TextField
                      name={REQUEST_URL}
                      label={'Request URL'}
                      helper={'Add your params to URL as key={value}'}
                      multiline={true}
                      minRows={3}
                      padding={0}
                    />
                  </Grid>
                  <Grid container item xs={1} justifyContent={'flex-end'}>
                    <RequestURLPicker
                      name={REQUEST_URL}
                      source={loadParameters}
                      padding={0}
                    />
                  </Grid>
                  <Conditions name={`${STRATEGIES}.[${index}].${CONDITIONS}`} />
                </Grid>
              ) : (
                <Grid container direction={'column'}>
                  <Grid item>
                    <Typography variant={'body2'}>
                      {stategy[REQUEST_URL]}
                    </Typography>
                  </Grid>
                  <Grid item>
                    {stategy[CONDITIONS].map((condition) => (
                      <>
                        <Typography variant={'body2'} component={'span'}>
                          {TargetsMap[condition[TARGET]]}:{' '}
                        </Typography>
                        <Typography variant={'caption'}>
                          <PreviewEntities
                            target={condition[TARGET]}
                            value={condition[ENTITIES]}
                          />
                        </Typography>
                      </>
                    ))}
                  </Grid>
                </Grid>
              )}
            </CardContent>
          </Card>
        </Grid>
      </FormFieldGroup>
    );
  },
  isEqual
);
